import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChange, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { ProjectInterface, ProjectRoleInterface } from '@rappider/api-sdk';
import { BreadcrumbOption, CrudFormConfig, HeadingComponentConfig } from '@rappider/rappider-components/utils';
import { PersonInvitationInviteRequestDto } from '@rappider/rappider-sdk';
import { INVITED_USER_LIST_CONFIG, INVITE_USER_VIA_MAIL_CONFIG } from '@rappider/shared/configs';
import { defaultToolbarTitleHeadingSize, PATH_DEFINITIONS } from '@rappider/shared/definitions';
import { PermissionService } from '@rappider/services';
import { cloneDeep } from 'lodash';
import { Subscription } from 'rxjs';
import { CancelInvitation, GetInvitedUsers, InviteUser, ResendInvitation } from '../../states/user-invitations-state/user-invitation.actions';
import { UserInvitationButtonMode } from './utils/user-invitations-button-mode.enum';
import { CommonModule } from '@angular/common';
import { RappiderEditFormModule, RappiderListGridModule, RappiderModalModule, RappiderTitleToolbarModule } from '@rappider/rappider-components';
import { TranslateModule } from '@ngx-translate/core';
import { selectCurrentUserProjectRole } from '../../states/selectors/user-role.selector';

@Component({
  selector: 'rappider-user-invitations',
  standalone: true,
  imports: [
    CommonModule,
    RappiderTitleToolbarModule,
    RappiderListGridModule,
    RappiderModalModule,
    RappiderEditFormModule,
    TranslateModule
  ],
  templateUrl: './user-invitations.component.html',
  styleUrls: ['./user-invitations.component.scss']
})
export class UserInvitationsComponent implements OnInit, OnChanges, OnDestroy {

  @Input() showTitle? = true;
  @Input() showInvitationModal?: boolean | string = false;

  /* loading status */
  isLoading: boolean;
  /* main title */
  mainTitle: HeadingComponentConfig;
  /* component title */
  title: string | string[] | BreadcrumbOption[];
  /* invited users list grid config */
  INVITED_USER_LIST_CONFIG = cloneDeep(INVITED_USER_LIST_CONFIG);
  INVITE_USER_CONFIG: CrudFormConfig;
  INVITE_USER_VIA_MAIL_CONFIG = INVITE_USER_VIA_MAIL_CONFIG;
  /* invitedUsers list */
  invitedUsers: any[];
  /* active project from state */
  activeProject: ProjectInterface;
  /* project members data */
  projectMembers: any[];
  /* current user data */
  activePerson: any;
  /* Subscriptions */
  subscriptions: Subscription[];
  /* can invite users */
  canInviteUsers = false;

  /* invite user modal variables */
  inviteUser: {
    title: string;
    visible: boolean;
    isSubmitted: boolean;
    isValid: boolean;
    fullScreen: boolean;
    data: PersonInvitationInviteRequestDto;
  } = {
      title: 'Invite User',
      visible: false,
      isSubmitted: false,
      isValid: false,
      fullScreen: true,
      data: { email: '', invitedFirstName: '', invitedLastName: '', isManager: false }
    };

  displayToolbar = false;
  displayToolbarBackButton = false;
  projectRoles: ProjectRoleInterface[];
  permissions: string;

  constructor(
    private store: Store<any>,
    private permissionService: PermissionService
  ) { }

  ngOnInit() {
    this.subscribeToData();
    if (this.showInvitationModal) {
      this.openInviteNewUserModal({ 'action': { 'name': 'Invite User' } });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.showInvitationModal?.currentValue) {
      this.openInviteNewUserModal({ 'action': { 'name': 'Invite User' } });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions?.forEach(subscription => subscription.unsubscribe());
  }

  /**
   * Subscribe to all data
   *
   * @memberof UserInvitationsComponent
   */
  subscribeToData() {
    this.subscriptions = [
      this.subscribeToPermissions(),
      this.subscribeToActiveProject(),
      this.subscribeToUserInvitation(),
      this.subscribeToLadingState()
    ];
  }

  /**
   * Subscribe to permissions
   *
   * @memberof UserInvitationsComponent
   */
  subscribeToPermissions() {
    return this.permissionService.hasPermission('invite-user')
      .subscribe(hasInvitePermission => {
        this.canInviteUsers = hasInvitePermission;
        this.updateInviteUserListConfig();
      });
  }

  /**
   * get active project from state and get invited users by active project id
   *
   * @return {*}
   * @memberof UserInvitationsComponent
   */
  subscribeToActiveProject() {
    return this.store.select(state => state.activeProject.data).subscribe(activeProject => {
      if (activeProject) {
        this.activeProject = activeProject;
        this.mainTitle = {
          content: 'Invited Users',
          type: defaultToolbarTitleHeadingSize
        };
        this.title = [
          {
            label: this.activeProject.name,
            redirectUrl: `${PATH_DEFINITIONS.PROJECTS.PROJECT_DETAIL_PATH}/${activeProject?.id}`
          },
          {
            label: 'Invited Users'
          }
        ];
        this.getInvitedUsers();
      }
    });
  }

  /**
   * Subscribe to user invitation state
   *
   * @return {*}
   * @memberof UserInvitationsComponent
   */
  subscribeToUserInvitation() {
    return this.store.select(state => state.userInvitation).subscribe(userInvitationState => {
      if (userInvitationState.data) {
        this.invitedUsers = userInvitationState.data;
      }
    });
  }

  subscribeToLadingState() {
    return this.store.select(state => state.userInvitation.isLoading).subscribe((isLoading: boolean) => {
      this.isLoading = isLoading;
    });
  }

  /**
   * Get Invited Users
   *
   * @memberof UserInvitationsComponent
   */
  getInvitedUsers() {
    this.store.dispatch(GetInvitedUsers());
  }

  /**
   * Open Invite User Modal
   *
   * @param {*} data
   * @memberof UserInvitationsComponent
   */
  openInviteNewUserModal(event) {
    const inviteUserEventName = event?.action?.name;
    if (inviteUserEventName === 'Invite User') {
      this.INVITE_USER_CONFIG = INVITE_USER_VIA_MAIL_CONFIG;
      this.inviteUser.visible = true;
    }
  }

  /**
   * Reset invite user modal and close invite user modal
   *
   *
   * @memberof UserInvitationsComponent
   */
  closeInviteUser() {
    this.inviteUser.visible = false;
    this.inviteUser.data = { email: '', invitedFirstName: '', invitedLastName: '', isManager: false };
    this.inviteUser.isValid = true;
    this.inviteUser.isSubmitted = false;
  }

  /**
   * Submit form
   *
   * @memberof UserInvitationsComponent
   */
  onInviteUserSubmit() {
    this.inviteUser.isSubmitted = true;
    if (this.inviteUser.isValid) {
      if (this.inviteUser.isSubmitted && this.inviteUser.isValid) {
        this.store.dispatch(InviteUser({
          payload: {
            invitedUser: {
              email: this.inviteUser.data.email,
              invitedFirstName: this.inviteUser.data.invitedFirstName,
              invitedLastName: this.inviteUser.data.invitedLastName,
              isManager: this.inviteUser.data.isManager
            }
          }
        }));
      }
      this.closeInviteUser();
    }
  }

  onInviteUserDataChange(userInvitationData: PersonInvitationInviteRequestDto) {
    this.inviteUser.data = userInvitationData;
  }

  onInviteUserIsValid(isValid: boolean) {
    this.inviteUser.isValid = isValid;
  }

  resendOrCancelInvitation(invitationData) {
    if (invitationData.action.name === UserInvitationButtonMode.Resend) {
      this.store.dispatch(ResendInvitation({ payload: { invitationId: invitationData.data.id } }));
    } else if (invitationData.action.name === UserInvitationButtonMode.Cancel) {
      this.store.dispatch(CancelInvitation({ payload: { invitationId: invitationData.data.id } }));
    }
  }

  /**
   * Update invite user list config based on permissions
   *
   * @memberof UserInvitationsComponent
   */
  updateInviteUserListConfig() {
    this.INVITED_USER_LIST_CONFIG = cloneDeep(INVITED_USER_LIST_CONFIG);

    if (this.INVITED_USER_LIST_CONFIG.listActions && this.INVITED_USER_LIST_CONFIG.listActions.length > 0) {
      const inviteUserButton = this.INVITED_USER_LIST_CONFIG.listActions.find(
        action => action.name === 'Invite User'
      );

      if (inviteUserButton) {
        inviteUserButton.disabled = !this.canInviteUsers;
        inviteUserButton.tooltipText = inviteUserButton.disabled ? 'You do not have permission to invite users.' : '';
      }
    }
  }
}
